import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Pagination as MuiPagination } from '@mui/material';
import {
  useGridApiRef,
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector,
  GridPagination,
} from '@mui/x-data-grid-pro';
import { LinearLoadingOverlay, Table } from '../../../components/table/Table';
import * as service from '../../../services/admin-service';
import { useAlerts } from '../../../hooks/use-alerts';
import { DownloadLink } from '../uploads/DownloadLink';

function Pagination({ page, onPageChange, className }) {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(event, newPage - 1);
      }}
    />
  );
}

function CustomPagination(props) {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
}

const columns = [
  {
    field: 'createdAt',
    headerName: 'Date',
    flex: 1,
    valueGetter: ({ row }) => row.createdAt && row.createdAt.split('T')[0],
  },
  {
    field: 'grower',
    headerName: 'Grower',
    flex: 1,
    valueGetter: ({ row }) => row.metadata?.grower,
  },
  {
    field: 'field',
    headerName: 'Field',
    flex: 1,
    valueGetter: ({ row }) => row.metadata?.field,
  },
  {
    field: 'farm',
    headerName: 'Farm',
    flex: 1,
    valueGetter: ({ row }) => row.metadata?.farm,
  },
  {
    field: 'orderId',
    headerName: 'Work Order ID',
    flex: 1,
    valueGetter: ({ row }) => row.soilSampleOrderId || 0,
    renderCell: ({ row }) => row.soilSampleOrderId || '❌',
  },
  {
    field: 'orderSampleCount',
    headerName: 'Work Order Point Count',
    flex: 1.2,
    renderCell: ({ row }) => {
      const orderCount = row.metadata?.orderSampleCount;
      const sampleCount = row.metadata?.sampleCount;
      const prefix =
        row.soilSampleOrderId && orderCount !== sampleCount ? '⚠️ ' : '';
      return `${prefix}${orderCount || ''}`;
    },
  },
  {
    field: 'sampleCount',
    headerName: 'Sampled Count',
    flex: 0.9,
    valueGetter: ({ row }) => row.metadata?.sampleCount,
  },
  {
    field: 'package',
    headerName: 'Package',
    flex: 1,
    valueGetter: ({ row }) => row.metadata?.package,
  },
  {
    field: 'submitter',
    headerName: 'PA',
    flex: 0.8,
    valueGetter: ({ row }) => row.metadata?.submitter,
  },
  {
    field: 'submitterEmail',
    headerName: 'Email Results To',
    flex: 1.2,
    valueGetter: ({ row }) => row.metadata?.submitterEmail,
  },
  {
    field: 'collectionDate',
    headerName: 'Collection Date',
    flex: 1,
    valueGetter: ({ row }) =>
      row.metadata?.collectionDate && row.metadata.collectionDate.split('T')[0],
  },
  {
    field: 'lab',
    headerName: 'Lab',
    flex: 1,
    valueGetter: ({ row }) => row.metadata?.lab,
  },
  {
    field: 'file',
    headerName: 'File',
    flex: 1.5,
    renderCell: ({ row }) => <DownloadLink url={row.file} />,
  },
];

export function Collections() {
  const fetching = useRef(false);
  const [isWorking, setWorking] = useState();
  const [collections, setCollections] = useState();
  const [totalCollections, setTotalCollections] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const { showError } = useAlerts();
  const apiRef = useGridApiRef();

  const fetchData = useCallback(() => {
    if (fetching.current) return;

    fetching.current = true;
    setWorking(true);

    service
      .getSoilCollections(paginationModel.page, paginationModel.pageSize)
      .then((response) => {
        setCollections(response.data);
        setTotalCollections(response.total);
      })
      .catch(showError)
      .finally(() => {
        fetching.current = false;
        setWorking(false);
      });
  }, [paginationModel.page, paginationModel.pageSize, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData, paginationModel.page]);

  return (
    <Box
      sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Table
        hideFooter={false}
        loading={isWorking || !collections}
        columns={columns}
        rows={collections || []}
        rowCount={totalCollections}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[100]}
        slots={{
          pagination: CustomPagination,
          loadingOverlay: LinearLoadingOverlay,
        }}
        apiRef={apiRef}
        initialState={{
          pinnedColumns: { right: ['file'] },
        }}
      />
    </Box>
  );
}
